import { useState, useMemo } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { Search, X } from 'lucide-react';
import { CALCULATORS, TRADES } from '@/lib/calculators';
import Seo from '@/components/Seo';

const titleFor = (name) => (/calculat/i.test(name) ? name : `${name} Calculator`);

export default function CalculatorsIndex() {
  const [searchParams] = useSearchParams();
  const [q, setQ] = useState(searchParams.get('q') || '');
  const query = q.trim().toLowerCase();

  const tradesWithCalcs = useMemo(() => {
    const match = (c) =>
      !query ||
      c.name.toLowerCase().includes(query) ||
      (c.blurb || '').toLowerCase().includes(query) ||
      c.slug.toLowerCase().includes(query) ||
      (TRADES.find((t) => t.slug === c.trade)?.name || '').toLowerCase().includes(query);
    return TRADES
      .map((t) => ({ ...t, calcs: CALCULATORS.filter((c) => c.trade === t.slug && match(c)) }))
      .filter((t) => t.calcs.length > 0);
  }, [query]);

  const resultCount = tradesWithCalcs.reduce((n, t) => n + t.calcs.length, 0);

  const jsonLd = {
    '@context': 'https://schema.org', '@type': 'CollectionPage',
    name: 'UK Trade Calculators', url: 'https://tradesmateuk.co.uk/calculators',
    description: `${CALCULATORS.length} free calculators for UK tradesmen — materials, quantities and estimating.`,
  };

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
      <Seo
        title="Free UK Trade Calculators — Materials & Estimating"
        description={`${CALCULATORS.length}+ free calculators for UK tradesmen: bricks, blocks, concrete, aggregate, paving, resin driveways, drainage, plaster, paint, tiles, roofing, electrical and more. Instant results, no sign-up.`}
        path="/calculators"
        jsonLd={jsonLd}
      />
      <header className="mb-6">
        <p className="text-xs uppercase tracking-widest text-brand font-semibold">{CALCULATORS.length} free tools</p>
        <h1 className="font-display font-semibold text-4xl sm:text-5xl uppercase tracking-tight mt-1">UK Trade Calculators</h1>
        <p className="text-slate-600 mt-3 max-w-2xl">Every calculator a UK tradesman needs — materials, quantities and estimating. Free, instant, and no sign-up required.</p>
      </header>

      {/* Search / filter */}
      <div className="sticky top-2 z-10 mb-8">
        <div className="relative">
          <Search size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" />
          <input
            data-testid="calc-search-input"
            type="text"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search 50+ calculators — try 'rafter', 'mortar', 'cable', 'BTU'…"
            className="tm-input pl-10 pr-10 w-full shadow-md"
            aria-label="Search calculators"
          />
          {q && (
            <button
              data-testid="calc-search-clear"
              onClick={() => setQ('')}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-brand"
              aria-label="Clear search"
            >
              <X size={18} />
            </button>
          )}
        </div>
        {query && (
          <p className="text-xs uppercase tracking-widest text-slate-500 mt-2 font-semibold" data-testid="calc-search-count">
            {resultCount} result{resultCount === 1 ? '' : 's'} for “{q}”
          </p>
        )}
      </div>

      {resultCount === 0 ? (
        <div className="tm-card p-8 text-center" data-testid="calc-no-results">
          <p className="font-display font-bold uppercase text-lg">No calculators match “{q}”</p>
          <p className="text-slate-600 text-sm mt-1">Try a different word, or <button onClick={() => setQ('')} className="text-brand underline">clear the search</button>. Missing a tool? <Link to="/feedback" className="text-brand underline">Request it here</Link>.</p>
        </div>
      ) : (
        <div className="space-y-10">
          {tradesWithCalcs.map((t) => (
            <section key={t.slug} data-testid={`calc-trade-section-${t.slug}`}>
              <h2 className="font-display font-bold text-xl uppercase mb-4 border-b border-slate-200 pb-2">{t.name}</h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
                {t.calcs.map((c) => (
                  <Link key={c.slug} to={`/calculators/${c.slug}`} data-testid={`calc-link-${c.slug}`} className="tm-card p-3 hover:shadow-lg transition-shadow flex items-center justify-between gap-2">
                    <span className="font-medium text-sm">{titleFor(c.name)}</span>
                    <span className="text-brand shrink-0">→</span>
                  </Link>
                ))}
              </div>
            </section>
          ))}
        </div>
      )}
    </div>
  );
}
